import { useState, useEffect } from 'react'
import { apiFetch } from '../services/auth'

interface Staff {
  id: string
  name: string
  email?: string
}

interface StaffAllocation {
  staffId: string
  staffName: string
  allocationPercentage: number
  hoursPerDay: number
}

interface MonthlyPlanning {
  month: number
  year: number
  sickDays: number
  vacationDays: number
  workDays: number
  actualHours?: number // Hours actually reported for this month
  isCompleted?: boolean // Whether this month is fully completed
}

interface Project {
  id: string
  description: string
  status?: number
}

interface PlanningData {
  id?: string
  name: string
  customerNumber: string
  customerName: string
  projectId: string
  projectName: string
  promisedHoursPerYear: number
  staffAllocations: StaffAllocation[]
  monthlyPlanning: MonthlyPlanning[]
  createdAt: string
  updatedAt: string
}

interface PlanningListItem {
  id: string
  name: string
  customerName: string
  projectName: string
  promisedHoursPerYear: number
  totalCapacity: number
  updatedAt: string
}

export function Planning() {
  const [plannings, setPlannings] = useState<PlanningListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [editingPlanning, setEditingPlanning] = useState<PlanningData | null>(null)

  const currentYear = new Date().getFullYear()
  const currentMonth = new Date().getMonth() + 1
  const months = [
    'Januari', 'Februari', 'Mars', 'April', 'Maj', 'Juni',
    'Juli', 'Augusti', 'September', 'Oktober', 'November', 'December'
  ]

  const fetchPlannings = async () => {
    setLoading(true)
    setError(null)
    
    try {
      const response = await apiFetch('/planning')

      // // console.log('Planning response status:', response.status)

      if (response.ok) {
        const data = await response.json()
        // // console.log('Planning data received:', data)
        
        // Handle empty array or no data
        if (!Array.isArray(data)) {
          // // console.log('Data is not an array:', typeof data)
          setPlannings([])
          return
        }

        // Transform the data to list items
        const planningList: PlanningListItem[] = data
          .filter((planning: any) => planning && planning.name) // Filter out invalid items
          .map((planning: PlanningData) => ({
            id: planning.id || `${planning.customerNumber}-${planning.projectId}`,
            name: planning.name || 'Unnamed Planning',
            customerName: planning.customerName || 'Unknown Customer',
            projectName: planning.projectName || 'Unknown Project',
            promisedHoursPerYear: planning.promisedHoursPerYear || 0,
            totalCapacity: typeof (planning as any).totalCapacity === 'number'
              ? (planning as any).totalCapacity
              : calculateCapacityForPlanning(planning),
            updatedAt: planning.updatedAt || new Date().toISOString()
          }))
        
        // // console.log('Transformed planning list:', planningList)
        setPlannings(planningList)
      } else if (response.status === 404) {
        // No plannings found
        // // console.log('No plannings found (404)')
        setPlannings([])
      } else {
        const errorText = await response.text()
        // // console.error('Planning API error:', response.status, errorText)
        throw new Error(`Failed to fetch plannings: ${response.status}`)
      }
    } catch (err) {
      // // console.error('Error fetching plannings:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch plannings')
      setPlannings([]) // Set empty array on error
    } finally {
      setLoading(false)
    }
  }

  const calculateCapacityForPlanning = (planning: PlanningData): number => {
    let totalCapacity = 0
    
    planning.monthlyPlanning.forEach(mp => {
      const adjustedWorkDays = mp.workDays * planning.staffAllocations.reduce((sum, allocation) => {
        return sum + (allocation.allocationPercentage / 100)
      }, 0)
      
      const availableDays = adjustedWorkDays - mp.sickDays - mp.vacationDays
      
      // Calculate average hours per day across all staff
      const totalAllocation = planning.staffAllocations.reduce((sum, allocation) => {
        return sum + (allocation.allocationPercentage / 100)
      }, 0)
      
      const averageHoursPerDay = planning.staffAllocations.reduce((sum, allocation) => {
        return sum + (allocation.hoursPerDay * (allocation.allocationPercentage / 100))
      }, 0) / (totalAllocation || 1)
      
      // availableDays already accounts for staff allocation, so just multiply by average hours
      totalCapacity += availableDays * averageHoursPerDay
    })
    
    return totalCapacity
  }

  const openNewPlanningModal = () => {
    setEditingPlanning(null)
    setShowModal(true)
  }

  const openEditPlanningModal = async (planningId: string) => {
    setLoading(true)
    try {
      const response = await apiFetch(`/planning/${planningId}`)

      if (response.ok) {
        const planning = await response.json() as PlanningData
        setEditingPlanning(planning)
        setShowModal(true)
      } else {
        throw new Error('Failed to load planning')
      }
    } catch (err) {
      // // console.error('Error loading planning:', err)
      setError(err instanceof Error ? err.message : 'Failed to load planning')
    } finally {
      setLoading(false)
    }
  }

  const deletePlanning = async (planningId: string) => {
    if (!confirm('Är du säker på att du vill ta bort denna planering?')) return

    try {
      const response = await apiFetch(`/planning/${planningId}`, {
        method: 'DELETE',
      })

      if (response.ok) {
        await fetchPlannings() // Refresh the list
      } else {
        throw new Error('Failed to delete planning')
      }
    } catch (err) {
      // // console.error('Error deleting planning:', err)
      setError(err instanceof Error ? err.message : 'Failed to delete planning')
    }
  }

  useEffect(() => {
    fetchPlannings()
  }, [])

  return (
    <div style={{
      backgroundColor: 'white',
      padding: '32px',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e2e8f0'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ color: '#1a202c', fontSize: '24px', fontWeight: '600', margin: 0 }}>
          Planeringar
        </h2>
        <button
          onClick={openNewPlanningModal}
          style={{
            backgroundColor: '#2563eb',
            color: 'white',
            border: 'none',
            padding: '12px 20px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <span style={{ fontSize: '18px' }}>+</span>
          Ny planering
        </button>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '24px'
        }}>
          <p style={{ color: '#dc2626', margin: '0' }}>{error}</p>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
          <p>Laddar planeringar...</p>
        </div>
      ) : plannings.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
          <p>Inga planeringar hittades. Klicka på "Ny planering" för att skapa en.</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8fafc' }}>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', fontWeight: '600', color: '#374151' }}>
                  Namn
                </th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', fontWeight: '600', color: '#374151' }}>
                  Kund
                </th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', fontWeight: '600', color: '#374151' }}>
                  Projekt
                </th>
                <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #e2e8f0', fontWeight: '600', color: '#374151' }}>
                  Utlovade timmar
                </th>
                <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #e2e8f0', fontWeight: '600', color: '#374151' }}>
                  Beräknad kapacitet
                </th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #e2e8f0', fontWeight: '600', color: '#374151' }}>
                  Senast uppdaterad
                </th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #e2e8f0', fontWeight: '600', color: '#374151' }}>
                  Åtgärder
                </th>
              </tr>
            </thead>
            <tbody>
              {plannings.map((planning, index) => (
                <tr key={planning.id} style={{
                  backgroundColor: index % 2 === 0 ? 'white' : '#f8fafc',
                  borderBottom: '1px solid #e2e8f0'
                }}>
                  <td style={{ padding: '12px', color: '#374151', fontWeight: '500' }}>
                    {planning.name}
                  </td>
                  <td style={{ padding: '12px', color: '#374151' }}>
                    {planning.customerName}
                  </td>
                  <td style={{ padding: '12px', color: '#374151' }}>
                    {planning.projectName}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right', color: '#374151' }}>
                    {planning.promisedHoursPerYear.toLocaleString()} h
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right', color: '#374151' }}>
                    {Math.round(planning.totalCapacity).toLocaleString()} h
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center', color: '#6b7280', fontSize: '14px' }}>
                    {new Date(planning.updatedAt).toLocaleDateString('sv-SE')}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                      <button
                        onClick={() => openEditPlanningModal(planning.id)}
                        style={{
                          backgroundColor: '#059669',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Redigera
                      </button>
                      <button
                        onClick={() => deletePlanning(planning.id)}
                        style={{
                          backgroundColor: '#dc2626',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Ta bort
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Planning Modal */}
      {showModal && (
        <PlanningModal
          planning={editingPlanning}
          onClose={() => setShowModal(false)}
          onSave={() => {
            setShowModal(false)
            fetchPlannings()
          }}
        />
      )}
    </div>
  )
}

// Planning Modal Component
function PlanningModal({ 
  planning, 
  onClose, 
  onSave 
}: { 
  planning: PlanningData | null
  onClose: () => void
  onSave: () => void
}) {
  const [customers, setCustomers] = useState<any[]>([])
  const [projects, setProjects] = useState<Project[]>([])
  const [staff, setStaff] = useState<Staff[]>([])
  const [selectedCustomer, setSelectedCustomer] = useState<string>(planning?.customerNumber || '')
  const [selectedProject, setSelectedProject] = useState<string>(planning?.projectId || '')
  const [planningName, setPlanningName] = useState<string>(planning?.name || '')
  const [loading, setLoading] = useState(false)
  const [loadingProjects, setLoadingProjects] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  // Form state
  const [promisedHours, setPromisedHours] = useState<number>(planning?.promisedHoursPerYear || 0)
  const [staffAllocations, setStaffAllocations] = useState<StaffAllocation[]>(planning?.staffAllocations || [])
  const [monthlyPlanning, setMonthlyPlanning] = useState<MonthlyPlanning[]>(planning?.monthlyPlanning || [])
  const [timeReportData, setTimeReportData] = useState<any[]>([])

  const currentYear = new Date().getFullYear()
  const currentMonth = new Date().getMonth() + 1
  const months = [
    'Januari', 'Februari', 'Mars', 'April', 'Maj', 'Juni',
    'Juli', 'Augusti', 'September', 'Oktober', 'November', 'December'
  ]

  const getWorkDaysInMonth = (year: number, month: number): number => {
    const date = new Date(year, month - 1, 1)
    const lastDay = new Date(year, month, 0).getDate()
    let workDays = 0

    for (let day = 1; day <= lastDay; day++) {
      date.setDate(day)
      const dayOfWeek = date.getDay()
      if (dayOfWeek !== 0 && dayOfWeek !== 6) { // Not Sunday (0) or Saturday (6)
        workDays++
      }
    }
    return workDays
  }

  const getAdjustedWorkDays = (baseWorkDays: number): number => {
    const totalAllocation = staffAllocations.reduce((sum, allocation) => {
      return sum + (allocation.allocationPercentage / 100)
    }, 0)
    
    return baseWorkDays * totalAllocation
  }

  const getEstimatedHoursForMonth = (mp: MonthlyPlanning): number => {
    const adjustedWorkDays = getAdjustedWorkDays(mp.workDays)
    const availableDays = adjustedWorkDays - mp.sickDays - mp.vacationDays
    
    // Calculate average hours per day across all staff
    const totalAllocation = staffAllocations.reduce((sum, allocation) => {
      return sum + (allocation.allocationPercentage / 100)
    }, 0)
    
    const averageHoursPerDay = staffAllocations.reduce((sum, allocation) => {
      return sum + (allocation.hoursPerDay * (allocation.allocationPercentage / 100))
    }, 0) / (totalAllocation || 1)
    
    // availableDays already accounts for staff allocation, so just multiply by average hours
    return availableDays * averageHoursPerDay
  }

  const isMonthCompleted = (year: number, month: number): boolean => {
    const now = new Date()
    const monthDate = new Date(year, month - 1, 1)
    const nextMonth = new Date(year, month, 1)
    return now >= nextMonth
  }

  const fetchTimeReports = async (customerNumber: string, projectId: string) => {
    if (!customerNumber || !projectId) return

    try {
      setError(null)
      const fromDate = new Date(Date.UTC(currentYear, 0, 1)).toISOString().slice(0, 10)
      const toDate = new Date(Date.UTC(currentYear, 11 + 1, 0)).toISOString().slice(0, 10)
      const query = new URLSearchParams({
        fromDate,
        toDate,
        customerNumber,
        projectId,
      })

      const response = await apiFetch(`/fortnox/time-reports?${query.toString()}`)

      if (!response.ok) {
        const errorText = await response.text()
        let errorMessage = errorText

        try {
          const parsedError = JSON.parse(errorText) as { error?: string }
          errorMessage = parsedError.error || errorText
        } catch {
          // keep raw text
        }

        setError(errorMessage || `Could not fetch time reports (${response.status})`)
        setTimeReportData([])
        return []
      }

      const data = await response.json()
      let registrations = []
      
      if (Array.isArray(data)) {
        registrations = data
      } else if (data.TimeRegistrations) {
        registrations = data.TimeRegistrations
      } else if (data.timeRegistrations) {
        registrations = data.timeRegistrations
      } else if (data.data) {
        registrations = Array.isArray(data.data) ? data.data : data.data.TimeRegistrations || []
      }

      const filteredRegistrations = registrations.filter((reg: any) => {
        const regCustomerNumber = reg.customer?.number || reg.customer?.id
        const regProjectId = reg.project?.id
        return regCustomerNumber === customerNumber && regProjectId === projectId
      })

      setTimeReportData(filteredRegistrations)
      return filteredRegistrations
    } catch (err) {
      // // console.error('Error fetching time reports:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch time reports')
      setTimeReportData([])
      return []
    }
  }

  const getActualHoursForMonth = (year: number, month: number): number => {
    return timeReportData
      .filter((reg: any) => {
        const workDate = reg.workedDate || reg.date
        if (!workDate) return false
        
        const date = new Date(workDate)
        return date.getFullYear() === year && date.getMonth() + 1 === month
      })
      .reduce((total: number, reg: any) => {
        const hours = reg.workedHours || reg.chargeHours || reg.duration || 0
        return total + hours
      }, 0)
  }

  const fetchCustomers = async () => {
    try {
      const response = await apiFetch('/fortnox/customers')

      if (!response.ok) {
        const errorText = await response.text()
        let errorMessage = errorText

        try {
          const parsedError = JSON.parse(errorText) as { error?: string }
          errorMessage = parsedError.error || errorText
        } catch {
          // keep raw text
        }

        throw new Error(errorMessage || 'Failed to fetch customers')
      }
      
      const data = await response.json()
      setCustomers((data as any)?.Customers || [])
    } catch (err) {
      // // console.error('Error fetching customers:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch customers')
    }
  }

  const fetchStaff = async () => {
    try {
      const response = await apiFetch('/staff')

      if (!response.ok) {
        const errorText = await response.text()
        let errorMessage = errorText

        try {
          const parsedError = JSON.parse(errorText) as { error?: string }
          errorMessage = parsedError.error || errorText
        } catch {
          // keep raw text
        }

        throw new Error(errorMessage || 'Failed to fetch staff')
      }
      
      const data = await response.json()
      const employees = data.employees || []
      const staffList = employees.map((emp: any) => ({
        id: emp.EmployeeId || emp.id,
        name: `${emp.FirstName || ''} ${emp.LastName || ''}`.trim() || emp.name || 'Unknown',
        email: emp.Email || emp.email
      }))
      
      setStaff(staffList)
    } catch (err) {
      // // console.error('Error fetching staff:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch staff')
    }
  }

  const fetchProjects = async (customerNumber: string) => {
    if (!customerNumber) return

    setLoadingProjects(true)
    try {
      const response = await apiFetch('/fortnox/projects')

      if (!response.ok) {
        const errorText = await response.text()
        let errorMessage = errorText

        try {
          const parsedError = JSON.parse(errorText) as { error?: string }
          errorMessage = parsedError.error || errorText
        } catch {
          // keep raw text
        }

        throw new Error(errorMessage || 'Failed to fetch projects')
      }

      const data = await response.json()
      const projectsList = data.Projects || data.projects || []
      setProjects(projectsList.map((proj: any) => ({
        id: proj.ProjectNumber || proj.id,
        description: proj.Description || proj.description || `Project ${proj.ProjectNumber || proj.id}`,
        status: proj.Status || proj.status
      })))
    } catch (err) {
      // // console.error('Error fetching projects:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch projects')
      setProjects([])
    } finally {
      setLoadingProjects(false)
    }
  }

  const savePlanningData = async () => {
    if (!selectedCustomer || !selectedProject || !planningName.trim()) {
      setError('Alla fält måste fyllas i')
      return
    }

    setSaving(true)
    try {
      const customer = customers.find(c => c?.CustomerNumber === selectedCustomer)
      const project = projects.find(p => p.id === selectedProject)
      
      const planningPayload: PlanningData = {
        id: planning?.id,
        name: planningName.trim(),
        customerNumber: selectedCustomer,
        customerName: customer?.Name || '',
        projectId: selectedProject,
        projectName: project?.description || `Project ${selectedProject}`,
        promisedHoursPerYear: promisedHours,
        staffAllocations,
        monthlyPlanning,
        createdAt: planning?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }

      // // console.log('Saving planning data:', planningPayload)

      const url = planning?.id ? `/planning/${planning.id}` : '/planning'
      
      // // console.log('Save URL:', url)
      // // console.log('Method:', planning?.id ? 'PUT' : 'POST')
      
      const response = await apiFetch(url, {
        method: planning?.id ? 'PUT' : 'POST',
        body: JSON.stringify(planningPayload)
      })

      // // console.log('Save response status:', response.status)

      if (!response.ok) {
        const errorText = await response.text()
        // // console.error('Save response error:', errorText)
        
        if (response.status === 404) {
          throw new Error('Planning API not available yet - please wait for deployment')
        }
        throw new Error(`Failed to save planning data: ${response.status} - ${errorText}`)
      }
      
      const responseData = await response.json()
      // // console.log('Save response data:', responseData)
      
      onSave()
    } catch (err) {
      // // console.error('Error saving planning data:', err)
      setError(err instanceof Error ? err.message : 'Failed to save planning data')
    } finally {
      setSaving(false)
    }
  }

  const addStaffAllocation = () => {
    setStaffAllocations([...staffAllocations, {
      staffId: '',
      staffName: '',
      allocationPercentage: 100,
      hoursPerDay: 8
    }])
  }

  const updateStaffAllocation = (index: number, field: keyof StaffAllocation, value: any) => {
    const updated = [...staffAllocations]
    updated[index] = { ...updated[index], [field]: value }
    
    if (field === 'staffId') {
      const selectedStaff = staff.find(s => s.id === value)
      if (selectedStaff) {
        updated[index].staffName = selectedStaff.name
      }
    }
    
    setStaffAllocations(updated)
  }

  const removeStaffAllocation = (index: number) => {
    setStaffAllocations(staffAllocations.filter((_, i) => i !== index))
  }

  const updateMonthlyPlanning = (month: number, field: keyof MonthlyPlanning, value: number) => {
    const updated = monthlyPlanning.map(mp => 
      mp.month === month ? { ...mp, [field]: value } : mp
    )
    setMonthlyPlanning(updated)
  }

  const calculateTotalCapacity = () => {
    let totalCapacity = 0
    
    monthlyPlanning.forEach(mp => {
      if (mp.isCompleted && mp.actualHours !== undefined && mp.actualHours > 0) {
        // Use actual reported hours for completed months that have data
        totalCapacity += mp.actualHours
      } else {
        // Use estimated hours for:
        // 1. Future months
        // 2. Completed months without actual data (actualHours is 0 or undefined)
        // This includes vacation days and sick days in the calculation
        totalCapacity += getEstimatedHoursForMonth(mp)
      }
    })
    
    return totalCapacity
  }

  // Initialize monthly planning if not provided
  useEffect(() => {
    if (!planning && monthlyPlanning.length === 0) {
      const initialMonthly = months.map((_, index) => {
        const monthNum = index + 1
        const completed = isMonthCompleted(currentYear, monthNum)
        return {
          month: monthNum,
          year: currentYear,
          sickDays: 1,
          vacationDays: 2,
          workDays: getWorkDaysInMonth(currentYear, monthNum),
          actualHours: completed ? 0 : undefined,
          isCompleted: completed
        }
      })
      setMonthlyPlanning(initialMonthly)
    }
  }, [])

  useEffect(() => {
    fetchCustomers()
    fetchStaff()
  }, [])

  useEffect(() => {
    if (selectedCustomer) {
      fetchProjects(selectedCustomer)
      if (!planning) setSelectedProject('')
    }
  }, [selectedCustomer])

  useEffect(() => {
    if (selectedCustomer && selectedProject) {
      fetchTimeReports(selectedCustomer, selectedProject)
    }
  }, [selectedCustomer, selectedProject])

  useEffect(() => {
    // Update actualHours for completed months whenever timeReportData changes
    setMonthlyPlanning(prev => prev.map(mp => ({
      ...mp,
      actualHours: mp.isCompleted ? getActualHoursForMonth(mp.year, mp.month) : mp.actualHours
    })))
  }, [timeReportData])

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '32px',
        maxWidth: '1200px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ color: '#1a202c', fontSize: '24px', fontWeight: '600', margin: 0 }}>
            {planning ? 'Redigera planering' : 'Ny planering'}
          </h2>
          <button
            onClick={onClose}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#6b7280',
              padding: '4px'
            }}
          >
            ×
          </button>
        </div>

        {error && (
          <div style={{
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px'
          }}>
            <p style={{ color: '#dc2626', margin: '0' }}>{error}</p>
          </div>
        )}

        {/* Planning Name */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
            Planeringens namn *
          </label>
          <input
            type="text"
            value={planningName}
            onChange={(e) => setPlanningName(e.target.value)}
            placeholder="T.ex. Q1 2025 - Utvecklingsprojekt"
            style={{
              padding: '12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              width: '100%',
              maxWidth: '400px'
            }}
          />
        </div>

        {/* Customer and Project Selection */}
        <div style={{ display: 'flex', gap: '24px', marginBottom: '32px', flexWrap: 'wrap' }}>
          <div style={{ flex: '1', minWidth: '250px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
              Välj kund *
            </label>
            <select
              value={selectedCustomer}
              onChange={(e) => setSelectedCustomer(e.target.value)}
              style={{
                padding: '12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: 'white',
                width: '100%'
              }}
            >
              <option value="">Välj kund...</option>
              {customers.map(customer => (
                <option key={customer.CustomerNumber} value={customer.CustomerNumber}>
                  {customer.Name} ({customer.CustomerNumber})
                </option>
              ))}
            </select>
          </div>

          <div style={{ flex: '1', minWidth: '250px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
              Välj projekt *
            </label>
            <select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              disabled={!selectedCustomer || loadingProjects}
              style={{
                padding: '12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: !selectedCustomer || loadingProjects ? '#f9fafb' : 'white',
                width: '100%',
                cursor: !selectedCustomer || loadingProjects ? 'not-allowed' : 'pointer'
              }}
            >
              <option value="">
                {loadingProjects ? 'Laddar projekt...' : 'Välj projekt...'}
              </option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.description} ({project.id})
                </option>
              ))}
            </select>
          </div>
        </div>

        {selectedCustomer && selectedProject && (
          <>
            {/* Promised Hours */}
            <div style={{ marginBottom: '32px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                Utlovade timmar per år
              </label>
              <input
                type="number"
                value={promisedHours}
                onChange={(e) => setPromisedHours(Number(e.target.value))}
                style={{
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  width: '200px'
                }}
              />
            </div>

            {/* Staff Allocations */}
            <div style={{ marginBottom: '32px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', margin: 0 }}>
                  Personal allokering
                </h3>
                <button
                  onClick={addStaffAllocation}
                  style={{
                    backgroundColor: '#2563eb',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Lägg till personal
                </button>
              </div>

              {staffAllocations.map((allocation, index) => (
                <div key={index} style={{
                  display: 'flex',
                  gap: '16px',
                  alignItems: 'center',
                  marginBottom: '12px',
                  padding: '16px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px'
                }}>
                  <select
                    value={allocation.staffId}
                    onChange={(e) => updateStaffAllocation(index, 'staffId', e.target.value)}
                    style={{
                      padding: '8px',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '14px',
                      flex: '1'
                    }}
                  >
                    <option value="">Välj personal...</option>
                    {staff.map(person => (
                      <option key={person.id} value={person.id}>
                        {person.name}
                      </option>
                    ))}
                  </select>

                  <div>
                    <label style={{ fontSize: '12px', color: '#6b7280' }}>Allokering %</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={allocation.allocationPercentage}
                      onChange={(e) => updateStaffAllocation(index, 'allocationPercentage', Number(e.target.value))}
                      style={{
                        padding: '8px',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        fontSize: '14px',
                        width: '80px'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: '12px', color: '#6b7280' }}>Timmar/dag</label>
                    <input
                      type="number"
                      min="0"
                      max="24"
                      step="0.5"
                      value={allocation.hoursPerDay}
                      onChange={(e) => updateStaffAllocation(index, 'hoursPerDay', Number(e.target.value))}
                      style={{
                        padding: '8px',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        fontSize: '14px',
                        width: '80px'
                      }}
                    />
                  </div>

                  <button
                    onClick={() => removeStaffAllocation(index)}
                    style={{
                      backgroundColor: '#dc2626',
                      color: 'white',
                      border: 'none',
                      padding: '8px 12px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    Ta bort
                  </button>
                </div>
              ))}
            </div>

            {/* Monthly Planning */}
            <div style={{ marginBottom: '32px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '16px' }}>
                Månadsplanering {currentYear}
              </h3>
              
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8fafc' }}>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', fontWeight: '600', color: '#374151' }}>
                        Månad
                      </th>
                      <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #e2e8f0', fontWeight: '600', color: '#374151' }}>
                        Arbetsdagar (justerat)
                      </th>
                      <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #e2e8f0', fontWeight: '600', color: '#374151' }}>
                        Sjukdagar
                      </th>
                      <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #e2e8f0', fontWeight: '600', color: '#374151' }}>
                        Semesterdagar
                      </th>
                      <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #e2e8f0', fontWeight: '600', color: '#374151' }}>
                        Beräknade timmar
                      </th>
                      <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #e2e8f0', fontWeight: '600', color: '#374151' }}>
                        Rapporterade timmar
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyPlanning.map((mp, index) => (
                      <tr key={mp.month} style={{
                        backgroundColor: index % 2 === 0 ? 'white' : '#f8fafc',
                        borderBottom: '1px solid #e2e8f0'
                      }}>
                        <td style={{ padding: '12px', color: '#374151', fontWeight: '500' }}>
                          {months[mp.month - 1]}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center', color: '#374151' }}>
                          {getAdjustedWorkDays(mp.workDays).toFixed(1)}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <input
                            type="number"
                            min="0"
                            max={getAdjustedWorkDays(mp.workDays)}
                            value={mp.sickDays}
                            onChange={(e) => updateMonthlyPlanning(mp.month, 'sickDays', Number(e.target.value))}
                            style={{
                              padding: '4px 8px',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              fontSize: '14px',
                              width: '60px',
                              textAlign: 'center'
                            }}
                          />
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <input
                            type="number"
                            min="0"
                            max={getAdjustedWorkDays(mp.workDays)}
                            value={mp.vacationDays}
                            onChange={(e) => updateMonthlyPlanning(mp.month, 'vacationDays', Number(e.target.value))}
                            style={{
                              padding: '4px 8px',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              fontSize: '14px',
                              width: '60px',
                              textAlign: 'center'
                            }}
                          />
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center', color: '#374151', fontWeight: '500' }}>
                          {getEstimatedHoursForMonth(mp).toFixed(1)} h
                        </td>
                        <td style={{ 
                          padding: '12px', 
                          textAlign: 'center', 
                          color: mp.isCompleted ? '#059669' : '#6b7280',
                          fontWeight: mp.isCompleted ? '600' : '400'
                        }}>
                          {mp.isCompleted && mp.actualHours !== undefined 
                            ? `${mp.actualHours.toFixed(1)} h` 
                            : mp.isCompleted 
                              ? '0 h' 
                              : '-'
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Capacity Summary */}
            <div style={{
              backgroundColor: '#f0f9ff',
              border: '1px solid #bae6fd',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '24px'
            }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#0c4a6e', marginBottom: '12px' }}>
                Kapacitetssammanfattning
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                <div>
                  <div style={{ fontSize: '14px', color: '#0369a1', marginBottom: '4px' }}>Utlovade timmar</div>
                  <div style={{ fontSize: '24px', fontWeight: '600', color: '#0c4a6e' }}>{promisedHours.toLocaleString()}</div>
                </div>
                <div>
                  <div style={{ fontSize: '14px', color: '#059669', marginBottom: '4px' }}>Rapporterade timmar</div>
                  <div style={{ fontSize: '24px', fontWeight: '600', color: '#059669' }}>
                    {Math.round(monthlyPlanning.reduce((sum, mp) => sum + (mp.actualHours || 0), 0)).toLocaleString()}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '14px', color: '#0369a1', marginBottom: '4px' }}>Total kapacitet</div>
                  <div style={{ fontSize: '24px', fontWeight: '600', color: '#0c4a6e' }}>{Math.round(calculateTotalCapacity()).toLocaleString()}</div>
                </div>
                <div>
                  <div style={{ fontSize: '14px', color: '#0369a1', marginBottom: '4px' }}>Skillnad</div>
                  <div style={{ 
                    fontSize: '24px', 
                    fontWeight: '600', 
                    color: calculateTotalCapacity() >= promisedHours ? '#059669' : '#dc2626' 
                  }}>
                    {Math.round(calculateTotalCapacity() - promisedHours).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Modal Actions */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '24px', borderTop: '1px solid #e2e8f0' }}>
          <button
            onClick={onClose}
            style={{
              backgroundColor: '#f3f4f6',
              color: '#374151',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '500'
            }}
          >
            Avbryt
          </button>
          <button
            onClick={savePlanningData}
            disabled={saving || !selectedCustomer || !selectedProject || !planningName.trim()}
            style={{
              backgroundColor: saving || !selectedCustomer || !selectedProject || !planningName.trim() ? '#9ca3af' : '#059669',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              cursor: saving || !selectedCustomer || !selectedProject || !planningName.trim() ? 'not-allowed' : 'pointer',
              fontSize: '16px',
              fontWeight: '500'
            }}
          >
            {saving ? 'Sparar...' : 'Spara planering'}
          </button>
        </div>
      </div>
    </div>
  )
}
